import { useState } from "react";
import Container from "../common/Container";
import Button from "../common/Button";
import { site } from "../../data/siteData";
import { toast } from "react-toastify";
import { FaPhoneAlt, FaEnvelope, FaMapMarkerAlt, FaClock } from "react-icons/fa";
import emailjs from "@emailjs/browser";
import "../../styles/Contact.css";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    subject: "",
    message: "",
  });

  const [sending, setSending] = useState(false);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const validate = () => {
    if (!form.name.trim()) return "Please enter your name.";
    if (!form.phone.trim()) return "Please enter your phone number.";
    if (!form.email.trim()) return "Please enter a valid email.";
    if (!form.message.trim()) return "Please tell us about your requirements.";
    return "";
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) return toast.error(err);

    const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

    if (!SERVICE_ID || !TEMPLATE_ID || !PUBLIC_KEY) {
      toast.error("Email service is not configured. Please check your .env values.");
      return;
    }

    try {
      setSending(true);

      const time = new Date().toLocaleString();

      const templateParams = {
        name: form.name,
        email: form.email,
        phone: form.phone,
        service_type: form.subject || "Not specified",
        message: form.message,
        time,
        reply_to: form.email,
      };

      await emailjs.send(SERVICE_ID, TEMPLATE_ID, templateParams, PUBLIC_KEY);

      toast.success("Inquiry sent! We usually respond within 2 business hours.");
      setForm({ name: "", phone: "", email: "", subject: "", message: "" });
    } catch (error) {
      toast.error("Failed to send. Please try again or call us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="contact-section">
      <div className="grid-lines"></div>
      <div className="floating-object object-1"></div>
      <div className="floating-object object-2"></div>
      <div className="floating-object object-3"></div>

      <Container>
        <div className="contact-grid">
          <div className="contact-info-panel">
            <span className="contact-badge">Get In Touch</span>
            <h2 className="contact-main-title">
              Ready to get started? <br />
              Request a free quote.
            </h2>
            <p className="contact-subtext">
              Whether it's a residential deep clean or a commercial facility contract,
              our team is ready to provide a tailored solution for your space.
            </p>

            <div className="info-cards">
              <div className="info-item">
                <div className="info-icon"><FaPhoneAlt /></div>
                <div>
                  <h4>Call Us Directly</h4>
                  <p>{site.phone}</p>
                </div>
              </div>

              <div className="info-item">
                <div className="info-icon"><FaEnvelope /></div>
                <div>
                  <h4>Email Inquiry</h4>
                  <p>{site.email}</p>
                </div>
              </div>

              <div className="info-item">
                <div className="info-icon"><FaMapMarkerAlt /></div>
                <div>
                  <h4>Service Area</h4>
                  <p>{site.locationLine || "Melbourne & Surrounding Suburbs"}</p>
                </div>
              </div>

              <div className="info-item">
                <div className="info-icon"><FaClock /></div>
                <div>
                  <h4>Business Hours</h4>
                  <p>Mon - Fri: 8:00am - 6:00pm</p>
                </div>
              </div>
            </div>
          </div>

          <div className="contact-form-container">
            <form className="premium-form" onSubmit={onSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Full Name</label>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    placeholder="John Doe"
                    disabled={sending}
                  />
                </div>
                <div className="form-group">
                  <label>Phone Number</label>
                  <input
                    type="text"
                    name="phone"
                    value={form.phone}
                    onChange={onChange}
                    placeholder="04XX XXX XXX"
                    disabled={sending}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Email Address</label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={onChange}
                  placeholder="john@example.com.au"
                  disabled={sending}
                />
              </div>

              <div className="form-group">
                <label>Service Type</label>
                <select
                  name="subject"
                  value={form.subject}
                  onChange={onChange}
                  className="form-select"
                  disabled={sending}
                >
                  <option value="">Select a service</option>
                  <option value="Residential Cleaning">Residential Cleaning</option>
                  <option value="Commercial / Office">Commercial / Office</option>
                  <option value="End of Lease">End of Lease</option>
                  <option value="Specialised Service">Specialised Service</option>
                </select>
              </div>

              <div className="form-group">
                <label>Message / Requirements</label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  placeholder="Please provide details like property size, location, preferred date, and any specific requirements..."
                  disabled={sending}
                />
              </div>

              <Button
                type="submit"
                variant="primary"
                className="submit-btn-full"
                disabled={sending}
              >
                {sending ? "Sending..." : "Submit Request"}
              </Button>
            </form>
          </div>
        </div>
      </Container>
    </section>
  );
}
